import { MutationFunction } from '@apollo/client';
import {
  AlertModal,
  Button,
  Heading,
  Pill,
  Stack,
  TableColumn,
  Table,
  Text,
  useConfirmation,
} from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { FeatureFlagAuditLogDrawer, FeatureFlagAuditLogEntry } from './feature_flag_audit_log_drawer';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import {
  FeatureFlagMetadata,
  notEmpty,
  UpdateCompanyFeatureFlagMutation,
  UpdateCompanyFeatureFlagMutationVariables,
} from '../../lib/graphql';
import { embedURLsInText } from '../../lib/util/text';

export interface FeatureFlagsViewProps {
  allFlags: (FeatureFlagMetadata | null)[];

  onFlagsChange: MutationFunction<UpdateCompanyFeatureFlagMutation, UpdateCompanyFeatureFlagMutationVariables>;
  companyId: string;

  currentSetFlags: Record<string, boolean>;
  auditLog: FeatureFlagAuditLogEntry[];
  loadMoreAuditLogs: () => Promise<unknown> | undefined;
}

type SortKey = 'display_name' | 'status' | 'domains' | 'original';
type SortDirection = 'asc' | 'desc';

export const FeatureFlagsView: React.FC<FeatureFlagsViewProps> = ({
  allFlags,
  onFlagsChange,
  companyId,
  currentSetFlags,
  auditLog,
  loadMoreAuditLogs,
}) => {
  const isAdmin = useIsAdmin();
  const hasCompanyEditPermission = useHasScopes([BackofficeScope.companyWriteCompany]);
  const isPrivilegedUser = isAdmin || hasCompanyEditPermission;

  const [sortKey, setSortKey] = useState<SortKey>('original');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const { pending, confirm, cancel, proceed, context } = useConfirmation<{
    featureId: string;
    featureDisplayName: string;
  }>();

  const handleFeatureToggle = useCallback(
    async (featureId: string, featureDisplayName: string) => {
      const confirmed = await confirm({ featureId, featureDisplayName });

      if (confirmed) {
        const previousValue = currentSetFlags[featureId];
        const newValue = !previousValue;
        await onFlagsChange({
          variables: {
            companyId,
            featureFlag: featureId,
            value: newValue,
          },
        });
      }
    },
    [confirm, currentSetFlags, onFlagsChange, companyId],
  );

  const handleSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortKey(key);
        setSortDirection('asc');
      }
    },
    [sortKey, sortDirection],
  );

  const data = useMemo(() => {
    const prepared = allFlags.filter(notEmpty).map((flag, index) => ({
      id: flag.feature_flag_id,
      action: undefined,
      originalIndex: index,
      ...flag,
    }));

    return prepared.sort((a, b) => {
      let comparison = 0;

      switch (sortKey) {
        case 'original':
          comparison = a.originalIndex - b.originalIndex;
          break;
        case 'display_name':
          comparison = a.display_name.localeCompare(b.display_name);
          break;
        case 'status':
          comparison = Number(!!currentSetFlags[a.id]) - Number(!!currentSetFlags[b.id]);
          break;
        case 'domains': {
          comparison = (a.domains ?? []).length - (b.domains ?? []).length;
          break;
        }
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [allFlags, currentSetFlags, sortKey, sortDirection]);

  const actionName = currentSetFlags[context?.featureId ?? ''] ? 'disable' : 'enable';

  const getSortIndicator = useCallback(
    (key: SortKey) => {
      if (sortKey !== key) return null;
      return sortDirection === 'asc' ? '↑' : '↓';
    },
    [sortKey, sortDirection],
  );

  const resetSort = useCallback(() => {
    if (sortKey === 'original') {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey('original');
      setSortDirection('asc');
    }
  }, [sortKey, sortDirection]);

  const columns: TableColumn<
    FeatureFlagMetadata & {
      id: string;
      action?: () => void;
    }
  >[] = [
    {
      id: 'display_name',
      minWidth: '200px',
      className: 'company__featureFlags-headerContainer',
      title: (
        <button onClick={() => handleSort('display_name')} className="company__featureFlags-headerButton">
          Name {getSortIndicator('display_name')}
        </button>
      ),
      cellContent: ({ value }) => <Text weight="bold">{typeof value === 'function' ? null : value}</Text>,
    },
    {
      id: 'action',
      width: '175px',
      className: 'company__featureFlags-headerContainer',
      title: (
        <button onClick={() => handleSort('status')} className="company__featureFlags-headerButton">
          Status {getSortIndicator('status')}
        </button>
      ),
      cellContent: ({ row }) => {
        const isFlagOn = !!currentSetFlags[row.id];
        const statusPill = {
          kind: isFlagOn ? ('success' as const) : ('neutral' as const),
          label: isFlagOn ? 'Enabled' : 'Disabled',
        };
        const actionLabel = isFlagOn ? 'Disable' : 'Enable';

        return (
          <Stack justifyContent="space-between">
            <Pill kind={statusPill.kind}>{statusPill.label}</Pill>
            <Button
              kind="subtle"
              disabled={row.id === 'enable_carrier_telematics_api' ? !isAdmin : !isPrivilegedUser}
              ref={buttonRef}
              onClick={() => handleFeatureToggle(row.id, row.display_name)}>
              {actionLabel}
            </Button>
          </Stack>
        );
      },
    },
    {
      id: 'domains',
      width: '150px',
      className: 'company__featureFlags-headerContainer',
      title: (
        <button onClick={() => handleSort('domains')} className="company__featureFlags-headerButton">
          Domain(s) {getSortIndicator('domains')}
        </button>
      ),
      cellContent: ({ value, row }) => {
        if (!Array.isArray(value)) {
          return null;
        }
        return (
          <Stack>
            {value.map((tag) => (
              <Pill key={row.id + tag}>{tag}</Pill>
            ))}
          </Stack>
        );
      },
    },
    {
      id: 'description',
      minWidth: '250px',
      title: 'Description',
      cellContent: ({ value }) => {
        if (!value || typeof value !== 'string') {
          return null;
        }
        return <Text style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{embedURLsInText(value)}</Text>;
      },
    },
    {
      id: 'usage',
      minWidth: '250px',
      title: 'Usage',
      cellContent: ({ value }) => {
        if (!value || typeof value !== 'string') {
          return '-';
        }
        return <Text style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{embedURLsInText(value)}</Text>;
      },
    },
  ];

  return (
    <div className="company__featureFlags-container">
      <Stack justifyContent="space-between">
        <Heading>Feature Flags</Heading>
        <Stack alignItems="center" gap="small">
          <Button kind="neutral" onClick={resetSort}>
            {sortKey === 'original' ? `Date implemented ${sortDirection === 'asc' ? '↑' : '↓'}` : 'Reset sort'}
          </Button>
          {auditLog !== null && (
            <FeatureFlagAuditLogDrawer allFlags={allFlags} auditLog={auditLog} loadMoreAuditLogs={loadMoreAuditLogs} />
          )}
        </Stack>
      </Stack>
      <Table className="company__featureFlags-list" data={data} columns={columns} />
      <AlertModal
        open={pending}
        onClose={cancel}
        description={
          context?.featureId === 'enable_carrier_telematics_api' && actionName === 'enable' ? (
            <Text>
              This API <Text weight="bold">must not be used any longer</Text> for regular carrier integrations. If the
              carrier doesn&apos;t use a regular vehicle FMS/ELD integration, then the carrier needs to send Visibility
              data with TP RTV API on a transport, see{' '}
              <a
                href="https://transporeon-hcskb.atlassian.net/wiki/spaces/ADPD/pages/27234193/Send+Visibility+Data+from+your+Inhouse+System"
                target="_blank"
                rel="noopener noreferrer">
                here
              </a>
              .
            </Text>
          ) : (
            <Text>
              <Text>Are you sure you want to </Text>
              <Text weight="bold">{actionName}</Text>
              <Text> the following feature flag: </Text>
              <Text weight="bold">{context?.featureDisplayName ?? ''}</Text>
              <Text>?</Text>
            </Text>
          )
        }
        title={
          context?.featureId === 'enable_carrier_telematics_api' && actionName === 'enable'
            ? 'Are you sure you want to enable a legacy API?'
            : 'Confirm feature flag value change'
        }
        trigger={buttonRef.current}>
        <Button kind="subtle" onClick={cancel}>
          Cancel
        </Button>
        <Button kind="primary" onClick={proceed}>
          Confirm
        </Button>
      </AlertModal>
    </div>
  );
};
